import React, {useEffect} from 'react';
import NavBar from '../Components/Nav/NavBar';
import {Outlet, useLocation, useNavigate} from 'react-router-dom';
import {useGetSessionQuery, useGetStorefrontQuery, useLazyGetPermissionsQuery} from '../Api/Api';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../Store/Store';
import {setPermissions, setSession, setToken} from '../Store/AuthSlice';
import Footer from '../Components/Nav/Footer';
import {pageBackgroundActive, pageBackgroundCss, parseGlobalStyleConfig, spinnerStyleCss} from '../Components/Helpers/globalStyleConfig';
import {hasRenderableBanner, parseHomePageConfig} from '../Components/Helpers/homePageConfig';
import {findPageBySlug} from '../Components/Helpers/pagesConfig';
import {paletteCssVars, setStorefrontStyles} from '../Components/Helpers/stylesConfig';
import {useIsNarrow} from '../Components/Helpers/useIsNarrow';

type LayoutProps = {
};

const Layout: React.FC<LayoutProps> = () => {
    // Publish `--sf-vw` — one percent of the surface the page is laid out on. index.css declares it
    // as `1vw` so it is never undefined; this narrows it to the SCROLLBAR-FREE width.
    //
    // `vw` deliberately includes the classic scrollbar, so on a scrolling page `50vw` overshoots the
    // real centre by half the scrollbar and a full-bleed element lands off-centre — measured as a
    // 59px gap on the left against 74px on the right at 3840px wide, which is exactly the 15px
    // scrollbar split in two. documentElement.clientWidth excludes it, so the two gaps match.
    // (The admin editor publishes the same property from its canvas width — see PreviewCanvas.)
    React.useLayoutEffect(() => {
        const publish = () => {
            const width = document.documentElement.clientWidth;
            if (width > 0) document.documentElement.style.setProperty('--sf-vw', `${width / 100}px`);
        };
        publish();
        // ResizeObserver, not a resize listener: the scrollbar appears when the page grows past the
        // viewport, which changes clientWidth WITHOUT firing `resize`. Listening for resize alone
        // left the value at the pre-scrollbar width and the gap short by half a scrollbar.
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', publish);
            return () => window.removeEventListener('resize', publish);
        }
        const observer = new ResizeObserver(publish);
        observer.observe(document.documentElement);
        return () => observer.disconnect();
    }, []);

    const storefrontSettings = useGetStorefrontQuery();
    const token = useSelector((state: RootState) => state.auth.token);
    const session = useSelector((state: RootState) => state.auth.session);
    const selectedCartId = useSelector((state: RootState) => state.cart.selectedCartId);
    const nav = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const [permissions] = useLazyGetPermissionsQuery();
    // Publish the storefront's named element style presets to the module registry the style builders
    // resolve `$styleRef` against (see Components/Helpers/stylesConfig.ts). Api.ts sets this from
    // transformResponse when the settings arrive; doing it here as well covers a warm RTK cache
    // (where transformResponse doesn't run again) and guarantees the registry is populated BEFORE
    // this render's children — hence during render, not in an effect. The setter is idempotent and
    // skips work entirely when the blob is unchanged.
    setStorefrontStyles(storefrontSettings.data?.stylesConfig);
    useEffect(() => {
        if(token){
            permissions().then(response => {
                if(response.data) {
                    dispatch(setPermissions(response.data));
                }
            })
        }
    }, [token]);

    // Rehydrate the session (e.g. after a reload) so access flags are available app-wide.
    const sessionQuery = useGetSessionQuery({ token: token ?? undefined }, { skip: !token || !!session });
    useEffect(() => {
        if (sessionQuery.data && !session) {
            dispatch(setSession(sessionQuery.data));
        }
    }, [sessionQuery.data, session, dispatch]);

    // A user granted access only to a specific prepared cart has no general storefront
    // access. If they have no cart to view, kick them out to login entirely. The
    // prepared-cart route is exempt — it validates its own access and sets the cart id.
    useEffect(() => {
        if (session?.hasGeneralStorefrontAccess !== false) return;
        if (selectedCartId) return;
        if (location.pathname.startsWith('/prepared-cart/')) return;
        const org = localStorage.getItem('organization');
        const sf = localStorage.getItem('storefront');
        const redirect = encodeURIComponent(location.pathname);
        if (org && sf) nav(`/${org}/${sf}/login?redirect=${redirect}`);
        else nav(`/login?redirect=${redirect}`);
    }, [session, selectedCartId, location.pathname, nav]);

    // Add global style for box-sizing
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
      html, body, #root {
        box-sizing: border-box !important;
        /* clip (not hidden) keeps horizontal overflow clipped WITHOUT turning these into
           scroll containers — a scroll-container ancestor that isn't the element actually
           scrolling breaks position: sticky (the sticky search chrome). */
        overflow-x: clip !important;
      }
      /* index.css sets body overflow auto; that overflow-y: auto makes body a scroll container
         that isn't the one actually scrolling, which defeats sticky. Keep the vertical axis
         visible so the page scroll stays on html and sticky binds to the viewport. */
      body {
        overflow-y: visible !important;
      }
      *, *:before, *:after {
        box-sizing: inherit !important;
      }
    `;
        document.head.appendChild(style);
        return () => { document.head.removeChild(style); };
    }, []);

    // The active palette / font pack published as `:root` custom properties (--sf-accent,
    // --sf-accent-rgb, --sf-font-heading, …) so a storefront's customCSS — and any hand-written rule
    // in it — can reference the brand without hard-coding hex values. Declared BEFORE the customCSS
    // <link> effect below so the link lands later in <head>: a merchant redefining one of these vars
    // in their own stylesheet then wins the cascade. Absent palette + font pack = no block at all.
    useEffect(() => {
        const css = paletteCssVars();
        if (!css) return;
        const style = document.createElement('style');
        style.setAttribute('data-sf-palette', '');
        style.innerHTML = css;
        document.head.appendChild(style);
        return () => { document.head.removeChild(style); };
    }, [storefrontSettings.data?.stylesConfig]);

    useEffect(() => {
        if(storefrontSettings.data?.favicon){
            let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.getElementsByTagName('head')[0].appendChild(link);
            }
            link.href = storefrontSettings.data.favicon;
        }
        if (storefrontSettings.data?.customCSS) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = storefrontSettings.data.customCSS;
            document.head.appendChild(link);

            return () => {
                document.head.removeChild(link);
            };
        } else if (storefrontSettings.data?.backgroundColor) {
            if (isDarkColor(storefrontSettings.data.backgroundColor)) {
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = '/themes/custom_dark.css';
                document.head.appendChild(link);
                return () => {
                    document.head.removeChild(link);
                };
            } else {
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = '/themes/custom_light.css';
                document.head.appendChild(link);
                return () => {
                    document.head.removeChild(link);
                };
            }
        }
    }, [storefrontSettings.data]);

    // Animated main-body background (styleConfig.pageBackground) — a generic, config-driven effect
    // (e.g. a parallax starfield) painted on a fixed backdrop behind the page content. The effect
    // itself lives entirely in the storefront's config; this only wires whatever it defines.
    const pageBackground = React.useMemo(
        () => parseGlobalStyleConfig(storefrontSettings.data?.styleConfig).pageBackground,
        [storefrontSettings.data?.styleConfig]);
    // Opt-in full-width page layout (homePageConfig.fullWidthLayout): lets #content-inner span
    // the full viewport at all breakpoints instead of the default 90% column, so full-bleed
    // bands (banner/nav-pod band/section bands) can reach the browser edges. Absent/false =
    // #content-inner renders with no width/padding override (today's 90% CSS column, unchanged).
    const fullWidthLayout = React.useMemo(
        () => parseHomePageConfig(storefrontSettings.data?.homePageConfig).fullWidthLayout === true,
        [storefrontSettings.data?.homePageConfig]);
    // Opt-in "hero under transparent header" (bannerUnderHeader): when enabled AND the full-bleed
    // banner AND we're on desktop AND the page has a renderable banner, the header floats
    // transparently over the full-bleed hero. Applies on the home route (driven by homePageConfig)
    // and on a custom-page route (driven by that page's own hero fields — see pagesConfig /
    // HomePage's matching hero-source resolve). When active we drop #content's navbar clearance
    // (inline paddingTop:0 beats the CSS calc) so the hero sits flush at the very top, and tell
    // NavBar to switch to its transparent/solid-on-scroll mode.
    const isNarrow = useIsNarrow(700);
    const underHeaderHero = React.useMemo(() => {
        const cfg = parseHomePageConfig(storefrontSettings.data?.homePageConfig);
        const isHomeRoute = location.pathname === '/' || location.pathname === '/home';
        // Resolve a custom page from a single-segment path (the /:pageSlug route). Reserved routes
        // (/cart, /search, …) match ahead of /:pageSlug in the router, so a real custom-page slug
        // never collides; any other single segment simply resolves to undefined here.
        const segments = location.pathname.split('/').filter(Boolean);
        const customPage = (!isHomeRoute && segments.length === 1)
            ? findPageBySlug(storefrontSettings.data, segments[0])
            : undefined;
        // Hero source: the custom page's own entry on a custom-page route, else homePageConfig —
        // both carry the same five hero fields, so header chrome and page render read in lockstep.
        const src = customPage ?? cfg;
        const active = src.bannerUnderHeader === true
            && src.bannerFullWidth === true
            && !isNarrow
            && (isHomeRoute || !!customPage)
            && hasRenderableBanner(storefrontSettings.data, customPage?.id);
        // Optional overlay-bar tint/shadow for the not-scrolled state (see IHomePageConfig / page entry).
        return { active, barBackground: src.bannerUnderHeaderBackground, barShadow: src.bannerUnderHeaderShadow };
    }, [storefrontSettings.data, location.pathname, isNarrow]);
    const underHeaderActive = underHeaderHero.active;
    const hasPageBackground = pageBackgroundActive(pageBackground);
    // Optional speed control: when the config names a speedParam, read that URL query param and use
    // its numeric value to scale the animation (higher = faster). Absent/invalid = configured speed.
    const speedFactor = React.useMemo(() => {
        const name = pageBackground?.speedParam;
        if (!name) return 1;
        const raw = new URLSearchParams(location.search).get(name);
        const n = raw != null ? parseFloat(raw) : NaN;
        return isFinite(n) && n > 0 ? Math.min(n, 1000) : 1;
    }, [pageBackground, location.search]);
    useEffect(() => {
        const css = pageBackgroundCss(pageBackground, speedFactor);
        if (!css) return;
        const style = document.createElement('style');
        style.setAttribute('data-sf-page-bg', '');
        style.innerHTML = css;
        document.head.appendChild(style);
        return () => { document.head.removeChild(style); };
    }, [pageBackground, speedFactor]);

    // The storefront background color is applied to #content, but html/body/#root keep the browser
    // default (white). That shows through wherever #content doesn't paint — notably below the footer
    // (which has a 40px bottom margin), on short pages, and in scroll overscroll. Paint the root
    // elements with the same color so nothing white peeks through.
    //
    // When the animated backdrop is active it must be the thing painting the page: the backdrop sits
    // at z-index:-1, so an opaque body/#root would hide it (and reveal only their color). Make
    // body/#root transparent so the backdrop shows through, and keep the base color on <html> so
    // overscroll and any uncovered area still read as the space color rather than white.
    useEffect(() => {
        const bg = storefrontSettings.data?.backgroundColor;
        const base = pageBackground?.color || bg;
        if (!hasPageBackground && !bg) return;
        const root = document.getElementById('root');
        const prev = { html: document.documentElement.style.backgroundColor, body: document.body.style.backgroundColor, root: root?.style.backgroundColor };
        if (hasPageBackground) {
            document.documentElement.style.backgroundColor = base ?? '';
            document.body.style.backgroundColor = 'transparent';
            if (root) root.style.backgroundColor = 'transparent';
        } else {
            document.documentElement.style.backgroundColor = bg!;
            document.body.style.backgroundColor = bg!;
            if (root) root.style.backgroundColor = bg!;
        }
        return () => {
            document.documentElement.style.backgroundColor = prev.html;
            document.body.style.backgroundColor = prev.body;
            if (root) root.style.backgroundColor = prev.root ?? '';
        };
    }, [storefrontSettings.data?.backgroundColor, hasPageBackground, pageBackground]);

    // Storefront-wide loading spinner styling (styleConfig.spinner). Injected once as a scoped
    // <style> so it reaches every Fluent <Spinner> on the storefront (the full-page spinner and
    // the many inline spinners), not just FullPageSpinner. Absent config = no rule injected.
    useEffect(() => {
        const spinner = parseGlobalStyleConfig(storefrontSettings.data?.styleConfig).spinner;
        const css = spinnerStyleCss(spinner);
        if (!css) return;
        const style = document.createElement('style');
        style.setAttribute('data-sf-spinner', '');
        style.innerHTML = css;
        document.head.appendChild(style);
        return () => { document.head.removeChild(style); };
    }, [storefrontSettings.data?.styleConfig]);

    const hexToRgb = (hex: string) => {
        // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
        const fullHex = hex.length === 4
            ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
            : hex;

        const bigint = parseInt(fullHex.slice(1), 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = (bigint & 255);
        return { r, g, b };
    };

    const isDarkColor = (color: string) => {
        const { r, g, b } = hexToRgb(color);
        // Using the luminance formula to determine if the color is dark
        const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
        return luminance < 128;
    };
    if (!token) {
        // Check the local store
        const localToken = localStorage.getItem('token');
        const localOrganization = localStorage.getItem('organization');
        const localStorefront = localStorage.getItem('storefront');
        if (localToken) {
            // set the token
            dispatch(setToken(localToken));
        } else {
            if (localOrganization && localStorefront) {
                nav(`/${localOrganization}/${localStorefront}/login?redirect=${encodeURIComponent(location.pathname)}`);
            } else {
                nav(`/login?redirect=${encodeURIComponent(location.pathname)}`);
            }
        }
    }
    if (storefrontSettings.isLoading) {
        // make nice loading screen
        return <div>Loading...</div>;
    }
    if (storefrontSettings.data == null) {
        // figure out what to do here
        return <div>Storefront not found</div>;
    }

    return (
        <div>
            {/* Fixed animated backdrop behind everything (styleConfig.pageBackground). One div per
                configured layer; the scoped <style> above paints/animates them. */}
            {hasPageBackground &&
                <div className='sf-page-bg' aria-hidden='true'>
                    {pageBackground!.layers!.map((_, i) => (
                        <div key={i} className={`sf-page-bg-layer sf-page-bg-layer-${i}`} />
                    ))}
                </div>
            }
            <NavBar settings={storefrontSettings.data} underHeaderHero={underHeaderActive} underHeaderBarBackground={underHeaderHero.barBackground} underHeaderBarShadow={underHeaderHero.barShadow}/>
            {/* When the animated backdrop is on, #content must be transparent so it shows through the
                gaps between content panels (an opaque page background would hide it). */}
            <div id='content' style={{fontFamily: storefrontSettings.data?.defaultFont, backgroundColor: hasPageBackground ? 'transparent' : storefrontSettings.data?.backgroundColor, color: storefrontSettings.data?.textColor, overflowX: 'clip', boxSizing: 'border-box', ...(underHeaderActive ? {paddingTop: 0} : {})}}>
                {/* No overflowX clip here: the full-bleed bands (hero banner, nav-pod/section bands —
                    100vw + calc(50% - 50vw)) must escape this 90% column to reach the viewport edges.
                    #content above (full viewport width, overflowX: clip) still guards against
                    horizontal scrollbars. */}
                <div id='content-inner' style={{boxSizing: 'border-box', ...(fullWidthLayout ? {width: '100%', padding: 0, maxWidth: 'none'} : {})}}>
                    <Outlet />
                </div>

            </div>
            {storefrontSettings.data?.includeFooter !== false &&
            <Footer settings={storefrontSettings.data}/>
            }
        </div>
    );
}

export default Layout;